// File: typescript/apps/front-end/src/main/react/card/GroupCardDisplayer.tsx
import DateFormatter from "@/main/common/DateFormatter";
import React from "react";
import {Localizer} from "@/main/react/Types";
import {type AnalyticsSurface} from "@/main/analytics/analyticsEvents";
import {GroupDTO,} from "@/generated-client";
import {Card, CardContent, CardFooter} from "@/components/ui/card";

import GroupButtons from "@/main/react/group/GroupButtons";
import {CtaButton} from "@/main/react/buttons/CtaButton";

import ProfileImage from "@/main/react/buttons/ProfileImage";
import {getDisplayUsername, getUserProfilePath} from "@/main/common/UsernameDisplayUtils";
import {CardMetadataBadges} from "@/main/react/card/CardMetadataBadges";
import { GROUP_ACTIVITY_ICONS, getGroupActivityItems } from "@/main/react/group/GroupActivityLine";
import {Activity, ArrowRight, CalendarDays, Lock, MessageSquareText} from "lucide-react";
import SensitiveBadge from "@/main/react/sensitive/SensitiveBadge";
import SensitiveMediaGuard from "@/main/react/sensitive/SensitiveMediaGuard";
import { getSensitivePreferenceMode, resolveSensitiveMaskingConfig, type SensitiveMaskingDirective } from "@/main/common/SensitivePreference";
import { getFriendlyCountBadgeLabel, getFriendlyPrivateBadgeLabel } from "@/main/react/card/CardMetadataBadgeCopy";
import AuthorAttribution from "@/main/react/component/AuthorAttribution";
import { MetaHandler } from "@/main/common/MetaHandler";
import { getMaskedContentWarningCopy } from "@/main/react/sensitive/ContentWarningCopy";


/**
 * `lastDiscussionAddedAt` is read straight off the generated `GroupDTO` since the 2026-09-14 client
 * refresh (CS-0); the hand-written recency extension type that bridged the 2026-07-22 spec was
 * deleted at that moment, as `groupDtoServerContract.spec.ts` demanded (ux-critique
 * `DISCOVERY-14`, 2026-09-02 correction). That guard still pins the Java getter the field comes from.
 *
 * <p>⚠️ Do not re-introduce a structural cast over the generated client here: a cast over a real
 * endpoint is only ever a receipt for a stale spec (`sensitive` and `mainLocale` were the precedent).
 */

/* ----------------------- Group Card ----------------------- */
export function GroupCardDisplayer(props: {
    group: GroupDTO;
    sensitiveMasking?: SensitiveMaskingDirective;
    /** Where this card is rendered, forwarded to GroupButtons for join/leave attribution. */
    surface?: AnalyticsSurface;
    localizer: Localizer;
    /** Extra classes merged into the card root (e.g. `h-full` for equal-height carousel rows). */
    className?: string;
    /**
     * The row's primary action is NAVIGATION, not publishing (ux-critique `DASHBOARD-12`).
     *
     * <p>Set it on a self-view membership list. There, `GroupButtons` resolves the same primary action
     * on every card, so the page is a column of identical filled-blue "Add post" buttons: on the
     * measured frame, 33% of every 192px row and the highest-contrast element in it, while the thing a
     * member came to do — enter a group — was an invisible `absolute inset-0` anchor with only a hover
     * wash, and on touch not even that.
     *
     * <p>It is a PRESENTATIONAL prop, in the same family as `GroupButtons`' own `alignStart` and
     * `stretchPrimary`, and it is passed straight through. Two things it deliberately is not: it is not
     * derived inside `GroupButtons`' state machine, which is shared with the group page, the directory
     * and the home rails where a trailing primary cluster is deliberate; and it is not the analytics
     * `surface` prop read as a presentation signal, which is the same mistake spelled differently.
     */
    demotePrimaryAction?: boolean;
    /**
     * `LANDING-95` (round 41 V68 a): a PROOF-ONLY card, for the marketing landing showcase. The card is
     * there to show a stranger what a thriving group looks like on a page whose one ask is "Create your
     * group", so it drops the whole action footer (`Open group`, `Join`, the overflow) and the entire card
     * becomes one link to the group: the overlay anchor re-enters the tab order and the accessibility
     * tree, named by the group, because it is now the card's only way in. Presentational, like
     * `demotePrimaryAction`; every other host keeps the footer.
     */
    proofOnly?: boolean;
}) {
    const {group, sensitiveMasking, surface = "page", localizer, className, demotePrimaryAction = false, proofOnly = false} = props;
    const [displayedGroup, setDisplayedGroup] = React.useState(group);
    const createdAt = (displayedGroup)?.createdAt;
    const df = React.useMemo(() => new DateFormatter(localizer), [localizer]);
    const displayCreatorUsername = getDisplayUsername(displayedGroup.creatorUsername, localizer);
    const creatorProfilePath = getUserProfilePath(displayedGroup.creatorUsername, "discussions");
    const groupName = displayedGroup.name ?? "";
    const groupDescription = displayedGroup.description?.trim() || localizer("group.no_description");
    // DISCOVERY-14: the date says what it is. This badge sits in the same row as "6 posts" and
    // "9 members", which are live counts, so a bare "1 hour ago" beside a calendar glyph scanned as
    // recent ACTIVITY when it is the founding date — inverting the one question a directory visitor
    // is actually asking. A group founded an hour ago with one post read as alive; a thriving
    // four-year-old group read "4 years ago" and looked dead. On a discovery card the format itself
    // is the claim, so the label is not decoration.
    //
    // group.card.created ("Created {0}") was already minted and translated in all 25 catalogs and
    // referenced by nothing.
    //
    // ⚠️ The sentence that stood here — "a real last-activity signal needs a field GroupDTO does not
    // carry; that half is parked with a question" — was FALSIFIED on 2026-08-26, when DISCOVERY-14
    // shipped exactly that field on every group surface. The founding date is therefore no longer the
    // best answer available, only the fallback one: DISCOVERY-79 (2026-09-02) prints it solely when
    // there is no last-post chip. See the badge row below.
    const createdAtLabel = createdAt ? localizer("group.card.created", df.timeSince(createdAt)) : "";

    // ux-critique DASHBOARD-27, then DISCOVERY-14 (closed 2026-08-26).
    //
    // ⚠️ The comment that stood here until 2026-09-02 said "every other producer of GroupDTO leaves
    // `lastDiscussionAddedAt` null, so the directory and the home rails draw no chip and this stays a
    // self-view signal". That was true when DASHBOARD-27 shipped it and FALSE from 2026-08-26, when
    // DISCOVERY-14 put the lateral aggregate into the shared `GROUP_SELECT_TEMPLATE` and into
    // `findRecommendedGroups`' hand-built projection. Every group surface sends it now — directory,
    // search, tag, related, recommended, the home rails and the self-view lists.
    //
    // A group that has never had a post still gets no chip, which is the honest rendering: "no posts
    // yet" is what the posts chip's own absence already says, and inventing a date for it would be
    // worse than saying nothing. That case is also the ONE place the founding date still prints — see
    // the badge row below (`DISCOVERY-79`).
    //
    const lastDiscussionAddedAt = displayedGroup.lastDiscussionAddedAt;
    const lastPostRelative = lastDiscussionAddedAt ? df.timeSince(lastDiscussionAddedAt) : "";
    const lastPostLabel = lastDiscussionAddedAt
        ? localizer("group.card.last_post", lastPostRelative)
        : "";
    // Honest community size (2026-09-10): the server-decided activity line, or "New group", or nothing.
    const activityItems = getGroupActivityItems(localizer, displayedGroup);
    // `DASHBOARD-101` (round 41 V3): "New group" and "Created 1 hour ago" answer the same question, so
    // when the activity line says `new` the founding-date fallback yields. One answer per card, on all
    // four card surfaces (directory, dashboard lists, home rails, landing showcase) since they share this.
    const activityIsNewGroup = activityItems.some((item) => item.key === "new");
    const isSensitive = Boolean(displayedGroup.sensitive);
    const { sensitivePreference, hideSensitiveMedia } = resolveSensitiveMaskingConfig(getSensitivePreferenceMode(), sensitiveMasking);
    const warningCopy = React.useMemo(
        () => getMaskedContentWarningCopy(localizer, { isSensitive }),
        [isSensitive, localizer],
    );
    const privateVisibilityLabel = getFriendlyPrivateBadgeLabel(localizer, "badge.group.private", displayedGroup.privateAccess);
    // Prefer the slug, fall back to the public id, and drop the overlay link entirely if neither
    // exists — a raw `group.slug` yields `/group/undefined` when the slug is null. Matches the
    // slug-or-publicId path target used by the home rails and other cards.
    const groupPathTarget = displayedGroup.slug?.trim() || displayedGroup.publicId?.trim();
    const groupHref = groupPathTarget ? `/group/${encodeURIComponent(groupPathTarget)}` : null;
    // A leader (and the creator, who may have handed leadership on) is never masked from their own
    // group. Both facts were already on the DTO / in the meta layer; nothing is threaded from a page.
    const loggedInUsername = MetaHandler.getLoggedInUsername();
    const isViewerGroupOwner = Boolean(displayedGroup.isUserLeaderOfGroup)
        || (Boolean(loggedInUsername) && displayedGroup.creatorUsername === loggedInUsername);

    /**
     * `DISCOVERY-74`, adjudicated in wave 19 §1.2: **for a signed-out visitor the card's primary
     * action is READ, not join.**
     *
     * On the measured anonymous frame every card's loudest element was a filled-primary `Join` that
     * kept its label at 412 px, while `Open group` — the only thing an anonymous visitor can actually
     * complete — collapsed to an unlabelled 39 px outline circle. `Join` is `requiresLogin`, so
     * twenty of the twenty loudest controls on the first page resolved to a signup wall, on a surface
     * whose declared audience is a stranger with no session.
     *
     * <p><b>Why the condition lives here and not at the call sites.</b> It is a property of the
     * VIEWER, not of the route: an anonymous visitor cannot complete a join on the directory, the home
     * rails, a profile's group list, the landing showcase or the related-groups block either. Keying
     * it off the surface would be one decision written five times, each free to drift. The signal is
     * already in hand — `loggedInUsername` is read directly above for the sensitive-media owner check.
     *
     * <p><b>Nothing is re-homed, and nothing is removed.</b> `Join` stays on the card, stays enabled
     * and still opens the account-access dialog; only the tier swaps. That is design-language rule 6's
     * "a quiet variant is a PRESENTATION, not a permission" used in the direction it is meant to go.
     * ⛔ Distinct from operator decision B12 (2026-08-20): the group's OWN page header keeps its filled
     * `Join`, and it is untouched here — `GroupPageHeaders` mounts `GroupButtons` directly and passes
     * no `demotePrimaryAction`.
     */
    const readIsTheCardsPrimaryAction = !loggedInUsername;

    /**
     * `DASHBOARD-117` (round 41 A12): on a SELF-VIEW list (the caller's `demotePrimaryAction`, whose doc
     * names exactly that host) the owner line naming the viewer themself tells them nothing, on every card
     * of their own "I lead" list. It is withheld only when the creator IS the viewer; the browse
     * directory, rails and showcase keep it, and so does a self-view card of a group someone else founded.
     * (The `/groups` zero-match suggestions also pass the prop, `EMPTY-STATES-129`; there the viewer's own
     * group losing a line that names the viewer is the same non-information, so the rule holds.)
     */
    const hideOwnerLine = demotePrimaryAction
        && Boolean(loggedInUsername)
        && displayedGroup.creatorUsername?.trim() === loggedInUsername;

    React.useEffect(() => {
        setDisplayedGroup(group);
    }, [group]);

    // The optimistic size adjustment (+1 on join, -1 on leave) went with the member count itself
    // (honest community size, 2026-09-10). The activity line shown instead is decided on the server and
    // is not something the browser can adjust.

    return (
        <Card
            /* No resting or `focus-within` shadow — the third and last of the card displayers to shed
               the dead `--surface-shadow-soft` declaration. The token is `none` in both themes, so this
               is a no-op visually; elevation is the hairline border (flat-X rule 1). Reasoning lives on
               `FEED_CARD_ROOT_CLASS` in `DiscussionCardDisplayer.tsx`. */
            className={`relative flex w-full max-w-full overflow-hidden rounded-xl border border-border py-0 card-hover gap-0 ${className ?? ""}`}
        >
                {/* `DISCOVERY-97` (S7 U60, compact phone card): below `sm` the card spends less height
                    on the same facts, so a phone screen compares several groups instead of one: a
                    tighter stack (`gap-2`), a smaller avatar and a two-line description. The name keeps
                    its two lines on purpose, because a generated group name's distinguishing tail is the
                    part a one-line clamp cuts (see the `h3` below). */}
                <CardContent className="px-3 py-3 sm:px-4 sm:py-4">
                    <div className="flex min-w-0 flex-col gap-2 sm:gap-3">
                        <div className="flex min-w-0 items-start gap-3 sm:gap-4">
                            {/* The guard covers the THUMBNAIL and nothing else. Wrapping the whole card
                                produced a blurred rectangle with one pill and no name, no author, no
                                member count and no description, while the footer beside it kept a fully
                                enabled Join — a commitment made blind, on a card that read as a row
                                which had failed to load (`EMPTY-STATES-31`, `GROUP-1`).

                                The scope is the product's own promise, not a judgement call:
                                `group.settings.sensitive_hint` tells the leader that viewers who hide
                                sensitive media "will see its thumbnails masked". The group's page is
                                separately gated; the card masks the picture. */}
                            <div className="flex shrink-0 items-start" data-playwright-id="group-card-avatar">
                                <SensitiveMediaGuard
                                    className="rounded-full"
                                    isSensitive={isSensitive}
                                    hideSensitiveMedia={hideSensitiveMedia}
                                    sensitivePreference={sensitivePreference}
                                    isViewerOwner={isViewerGroupOwner}
                                    variant="avatar"
                                    // The full-card link below is a sibling of this guard, so without this the masked scrim
                                    // covers it and a blurred card cannot be opened without revealing first. Design §8.6.
                                    overlayStacking="below-link-overlay"
                                    badgeLabel={warningCopy.badgeLabel}
                                    label={warningCopy.label}
                                    helperText={warningCopy.helperText}
                                    revealText={warningCopy.revealText}
                                    preferencesRouteText={warningCopy.preferencesRouteText}
                                >
                                    <ProfileImage localizer={localizer}
                                        imageUrl={displayedGroup.thumbnailUrl}
                                        username={groupName}
                                        mode="group"
                                        hoverable={false}
                                        className="size-12 sm:size-16"
                                    />
                                </SensitiveMediaGuard>
                            </div>

                            <div className="flex min-w-0 flex-1 flex-col gap-2">
                                <h3
                                    className="inline-flex min-w-0 flex-wrap items-center gap-2 text-base font-semibold leading-tight tracking-tight text-text-main sm:text-lg"
                                    data-playwright-id="group-card-name"
                                >
                                    {/* Wraps to two lines rather than truncating: at 412px the avatar and
                                        gaps leave ~288px for the heading, and the distinguishing tail of
                                        a generated group name was exactly the part being cut. */}
                                    <span className="line-clamp-2 min-w-0">{groupName}</span>
                                    {isSensitive && <SensitiveBadge className="py-0">{localizer("content-warning.badge.sensitive")}</SensitiveBadge>}
                                </h3>
                                {hideOwnerLine ? null : (
                                <AuthorAttribution localizer={localizer}
                                    name={displayCreatorUsername}
                                    href={creatorProfilePath}
                                    imageUrl={displayedGroup.creatorProfileImageUrl}
                                    avatarSeed={displayedGroup.creatorUsername ?? displayCreatorUsername}
                                    className="text-xs sm:text-sm"
                                    avatarClassName="size-5"
                                    // `DISCOVERY-68`. This card is the case the flag exists for: two
                                    // ~20 CSS px anchors sit directly under the group title, inside a
                                    // card whose entire surface is a link to a DIFFERENT destination,
                                    // and both are lifted above that link by `z-layer-raised-front`.
                                    // See the prop's own doc for why it is the expander rather than a
                                    // merged anchor, and for the pitch arithmetic that makes it safe.
                                    coarsePointerHitFloor={true}
                                />
                                )}
                            </div>
                        </div>

                        {/* `DASHBOARD-46` — the metadata row lives in the CARD's column, not in the
                            avatar's.

                            It used to sit inside the text column that starts after the avatar, while
                            the description and the footer below it use the card's full width. So the
                            row that needs width most had the least of it: at 412 CSS px a 253 px chip
                            column inside a 322 px content measure, a 21% loss, and the four-or-five
                            chips wrapped into a ragged 1-2-1 arrangement about 29% of the card's
                            height and roughly 1.8x the height of the two-line group name they
                            describe. A member scanning their own memberships to pick a group read the
                            shortest element on the card to decide and the tallest one to learn
                            nothing.

                            One DOM move, no magic constant and no responsive duplicate of the
                            component: at 412 px the row gains the 69 px it was indented by, and on
                            desktop the chips still occupy a single row, one rung lower in the reading
                            order. The decision is taken HERE rather than at a call site because the
                            same card serves the directory, the dashboard membership lists, the home
                            rails and the landing showcase. */}
                        <CardMetadataBadges
                            className="gap-1.5"
                            badgeClassName="border-border/60 bg-background2/50"
                            items={[
                                ...(privateVisibilityLabel ? [{
                                    key: "visibility",
                                    icon: <Lock className="size-3.5" aria-hidden={true}/>,
                                    label: privateVisibilityLabel,
                                }] : []),
                                /* `DISCOVERY-79` — exactly ONE time chip per card, and the founding
                                   date is the fallback rather than a peer.

                                   The card printed both, and they compete to answer the same
                                   question. `DISCOVERY-14`'s whole finding was that an unlabelled
                                   founding date was MISTAKEN for a recency signal, and that the one
                                   question a directory visitor is silently asking is "is this group
                                   alive?". Since 2026-08-26 the card carries a true answer to it on
                                   every group surface, so the created date is now the weaker of two
                                   chips answering a question nobody asked — while being the widest,
                                   and first, in a 348 CSS px phone column where four badges wrapped
                                   into three ragged rows worth 31% of the card.

                                   So it prints only when there is no last-post chip: a group that has
                                   never had a post, where the founding date is the only temporal fact
                                   there is and competes with nothing. Every card then carries exactly
                                   one, which is what fixes the RHYTHM the row is actually about — six
                                   stacked cards breaking their internal alignment in the same
                                   lopsided way.

                                   Not a styling change: the resting pill recipe is an operator
                                   decision (`DISCOVERY-48`) and `CardMetadataBadges` is untouched. No
                                   key was minted or retired; `group.card.created` stays live. */
                                ...(createdAtLabel && !lastPostLabel && !activityIsNewGroup ? [{
                                    key: "createdAt",
                                    icon: <CalendarDays className="size-3.5" aria-hidden={true}/>,
                                    label: createdAtLabel,
                                    // Hooked so the browser oracle can assert the EXCLUSIVITY rather
                                    // than only the presence of the survivor: "at most one time chip"
                                    // is unfalsifiable if only one of the two is findable.
                                    dataPlaywrightId: "group-card-created-badge",
                                }] : []),
                                ...(lastPostLabel ? [{
                                    key: "lastPost",
                                    icon: <Activity className="size-3.5" aria-hidden={true}/>,
                                    // `DISCOVERY-79` (round 41 V19 b): the chip PAINTS only the relative
                                    // time, without the "Last post" prefix. Measured 2026-09-07, the three
                                    // chips needed 369 CSS px against a 323 px column and the prefix was
                                    // the ~47 px over; the resting pill recipe is pinned (`DISCOVERY-48`),
                                    // so content is the only lever. The whole sentence stays the chip's
                                    // accessible text (sr-only), so `DISCOVERY-14`'s "say what the date
                                    // is" still holds for assistive tech, from the same translated key.
                                    label: (
                                        <>
                                            <span aria-hidden={true} data-group-card-last-post-visible="">{lastPostRelative}</span>
                                            <span className="sr-only">{lastPostLabel}</span>
                                        </>
                                    ),
                                    // `DISCOVERY-14`'s owed browser assertion. The chip's whole chain
                                    // — SQL lateral, DTO getter, Jackson, the card — was only ever
                                    // covered by a real-Postgres test, which cannot see a card.
                                    dataPlaywrightId: "group-card-last-post-badge",
                                }] : []),
                                /* `showZero` (`DISCOVERY-40`): without it a group with no
                                   posts renders TWO badges where every neighbouring card
                                   renders three, so "nothing has been posted here" and "the
                                   count failed to load" are the same silence — and the card
                                   that most needs to warn a visitor is the least informative
                                   one on the page. `getFriendlyCountBadgeLabel` still returns
                                   null for a null/non-finite count, which is what keeps those
                                   two cases distinct. Same option, same badge family, same
                                   "# post / # posts" plural as `SeriesCardDisplayer`'s
                                   `badge.series.discussions` — copying the sibling card's
                                   mechanism rather than minting a second one for one zero.
                                   The activity badges below never render a zero: the server sends
                                   no number for a half that is zero (honest community size). */
                                ...(getFriendlyCountBadgeLabel(localizer, "badge.group.discussions", displayedGroup.discussionsCount, {showZero: true}) ? [{
                                    key: "discussions",
                                    icon: <MessageSquareText className="size-3.5" aria-hidden={true}/>,
                                    label: getFriendlyCountBadgeLabel(localizer, "badge.group.discussions", displayedGroup.discussionsCount, {showZero: true}),
                                }] : []),
                                // Honest community size (2026-09-10): the server-decided activity
                                // line, or "New group", or nothing, where the member count was.
                                ...activityItems.map((item) => ({
                                    key: `activity-${item.key}`,
                                    icon: GROUP_ACTIVITY_ICONS[item.key],
                                    label: item.label,
                                    dataPlaywrightId: `group-card-activity-${item.key}`,
                                })),
                            ]}
                        />

                        {/* text-neutral is a FILL token (gray-400) and measured 2.40:1 here — it does not
                            even reach the 3:1 large-text floor, on the largest block of copy in the row.
                            text-neutral-text (gray-600) is the text half of that pair.

                            `line-clamp-3` replaces `series-card-text`, which was referenced here and in
                            SeriesCardDisplayer and defined NOWHERE — so the description was completely
                            unclamped inside a `grid sm:grid-cols-2` whose cells stretch to equal height,
                            and one long description could leave half its neighbour card as dead space
                            (ux-critique DISCOVERY-2). Plain Tailwind, the same mechanism the group name
                            two elements up already uses. */}
                        <p className="line-clamp-2 sm:line-clamp-3 wrap-anywhere text-sm leading-5 text-neutral-text sm:text-[15px]">
                            {groupDescription}
                        </p>

                        {Array.isArray(displayedGroup.tags) && displayedGroup.tags.length > 0 ? (
                            <div className="relative z-layer-raised-front flex flex-wrap gap-1.5" data-playwright-id="group-card-tags">
                                {displayedGroup.tags.slice(0, 4).map((tagName) => (
                                    <a
                                        key={tagName}
                                        href={`/groups?tag=${encodeURIComponent(tagName)}`}
                                        /* `EMPTY-STATES-75` / `DASHBOARD-48`, 2026-08-26 — `border-border`, undiluted.
                                           This chip is a real `<a href>` that navigates to
                                           the tag directory, so its edge is the visual boundary that identifies a
                                           user-interface component under WCAG 1.4.11's 3:1, not decoration. The
                                           `/60` composited `--line-color` down to roughly 2.2:1 light and 2.4:1
                                           dark over `bg-background2/50`, while the card hairline a few pixels away
                                           measured 3.91:1 and 4.49:1 from the same frames — so the one edge that
                                           had to clear the floor was the one painted below it. The fill rescues
                                           nothing: it is about 1.1:1 against the card either way. (The alpha suffix is
                                           named in the register, not restated here: a `/NN` written into a comment
                                           on a line carrying a focus-ring token would be a candidate for the
                                           very scanner this closes.)

                                           The fix is at the CALL SITE, never on `--border`, which is shared with
                                           `DASHBOARD-35` and `DISCOVERY-5` and is their joint decision.
                                           `borderTokenContrast.spec.ts` measures both themes and holds the
                                           inventory this file has now left. */
                                        /* `DISCOVERY-116` (S7 A90): `relative touch-hit-44`. At 412 px this chip is a
                                           22 px link painted over the card-wide link, so a near miss opened the group
                                           instead of the topic. The expander grows the TARGET to the 44 px floor on a
                                           coarse pointer without moving the chip; `relative` is its containing block. */
                                        className="standard-hover relative touch-hit-44 inline-flex max-w-full items-center rounded-full border border-border bg-background2/50 px-2 py-0.5 text-[11px] text-neutral-text sm:text-xs focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
                                    >
                                        <span className="truncate">#{tagName}</span>
                                    </a>
                                ))}
                            </div>
                        ) : null}
                    </div>
                </CardContent>

            {groupHref ? (
                // `focus-visible` treatment with a NEGATIVE outline offset (`EMPTY-STATES-41`). This
                // anchor is the only way to open the row with a keyboard and carried no focus treatment
                // at all. The negative offset is the load-bearing half: the card root is
                // `overflow-hidden` and an outline at offset 0 paints outside the border box, where it
                // can be clipped away entirely. One string across all five card families.
                // `DISCOVERY-98` (round 41 A26): a POINTER target only. The footer's labelled `Open group`
                // CTA below renders under the same `groupHref` condition and goes to the same place, so the
                // overlay leaves the tab order and the accessibility tree: one keyboard stop and one
                // links-list entry per card, not two (the `HomeDiscoveryRail` card's shape). The card
                // stays tappable anywhere. Its focus classes are kept inert for a programmatic focus.
                // `LANDING-95`: on a proof-only card there is no footer CTA, so the overlay is the card's one
                // keyboard stop and links-list entry, named by the group.
                <a
                    href={groupHref}
                    className="absolute inset-0 z-layer-base-front rounded-xl focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring"
                    aria-label={groupName}
                    tabIndex={proofOnly ? undefined : -1}
                    aria-hidden={proofOnly ? undefined : true}
                    data-playwright-id="group-card-overlay-link"
                ></a>
            ) : null}

            {/* `mt-auto` pins the action row to the bottom of the card (`EMPTY-STATES-53`, `LANDING-26`).
                The groups grid and the landing showcase both stretch every cell of a row to the tallest
                sibling, but each footer followed its own content, so the one repeated action in a
                20-card grid landed at an unpredictable height with up to ~66px of void under the shorter
                card's CTA. `Card` is already `flex flex-col`, so this is one property, and it is
                provably a no-op wherever the card sizes to its own content: there is no free space to
                distribute. */}
            {/* `LANDING-95`: a proof-only card has no action footer at all. */}
            {proofOnly ? null : (
            <CardFooter className="relative z-layer-raised-front mt-auto flex flex-row flex-wrap items-center justify-end gap-2 px-3 pb-3 pt-0 sm:px-4 sm:pb-4">
                {/* `DASHBOARD-12`, the half that needs no page context. Opening the group is what the row
                    exists for and it had no visible affordance at all — only an `absolute inset-0` anchor
                    and a `card-hover` wash, and on touch there is no hover, so a member was offered
                    exactly one visible button and it opens a post picker. This is the quiet leading tier
                    against the trailing primary cluster.

                    The label reuses `group.discussion_submission.review.open_group` ("Open group")
                    deliberately: a new string is a 24-locale bill against a catalog budget already at its
                    ceiling (AGENTS.md rule 11), and the same reuse is recorded on this card's overflow
                    trigger. It collapses to icon-only below `sm` so it cannot re-create `DASHBOARD-36`'s
                    wrap defect on the group card in a long locale.

                    The other half of `DASHBOARD-12` landed 2026-08-22 (wave 8, batch E): the caller now
                    says whether this is a self-view membership list, via `demotePrimaryAction` below.
                    It stays a caller decision precisely because the same `GroupButtons` serves the group
                    page, the directory and the home rails, where a trailing primary cluster is
                    deliberate. */}
                {groupHref ? (
                    <CtaButton
                        icon={ArrowRight}
                        text={localizer("group.discussion_submission.review.open_group")}
                        href={groupHref}
                        // `DISCOVERY-33`: `displayTextOnMobile={false}` resolves to `hidden sm:inline`
                        // on the label span, and `display: none` removes the text from the ACCESSIBLE
                        // NAME computation, not merely from the layout. The icon is `aria-hidden` by
                        // default and `CtaButton`'s `effectiveAriaLabel` falls through to `undefined`,
                        // so below 640px this link shipped with NO accessible name at all, on every
                        // card in the directory. The label survives the CSS collapse by being named
                        // here, with the very key the visible text already uses — no new key, and the
                        // icon-only mobile collapse (`DASHBOARD-12`, a recorded decision) is untouched.
                        ariaLabel={localizer("group.discussion_submission.review.open_group")}
                        // `DISCOVERY-74`: for a signed-out visitor this control keeps its label at
                        // 412px, because for that viewer it IS the card's primary action and an
                        // unlabelled 39px circle is not one. `DASHBOARD-62`'s collapse rule was taken
                        // to stop the footer wrapping in a long locale, and its pressure came from
                        // "Add post" — which only renders for a MEMBER, and therefore never in this
                        // branch. The signed-out footer is `Open group` + `Join` + the overflow
                        // trigger, well inside the 412px measure even in `fr`.
                        displayTextOnMobile={readIsTheCardsPrimaryAction}
                        size="sm"
                        // S7 U60 (`DISCOVERY-95`): the outline tier for EVERY viewer. `DISCOVERY-74` made
                        // this the filled primary for a signed-out visitor, which put twenty loud buttons
                        // on one directory page; the card itself is the tap target (the overlay anchor
                        // above), so the read no longer needs the loudest tier to be found. It keeps its
                        // visible label for that viewer, and `Join` stays demoted beside it.
                        variant="outline"
                        extraClass="group-card-open-button mr-auto"
                        playwrightId="group-card-open-button"
                    />
                ) : null}

                <GroupButtons
                    groupPublicId={group.publicId ?? ""}
                    groupSlug={displayedGroup.slug ?? ""}
                    compact={true}
                    initiallyMember={Boolean(displayedGroup.isUserPartOfGroup)}
                    initiallyLeader={Boolean(displayedGroup.isUserLeaderOfGroup)}
                    initiallyBanned={Boolean(displayedGroup.isUserBanned)}
                    initiallyRequestedToJoin={Boolean(displayedGroup.isUserRequestedToJoinGroup)}
                    groupName={displayedGroup.name ?? ""}
                    groupDescription={displayedGroup.description ?? ""}
                    groupThumbnailUrl={displayedGroup.thumbnailUrl ?? ""}
                    mainLocale={displayedGroup.mainLocale ?? undefined}
                    privateAccess={Boolean(displayedGroup.privateAccess)}
                    sensitive={isSensitive}
                    // `DISCOVERY-74` — the same prop, one more condition, decided by the HOST as its
                    // doc requires. The caller's own `demotePrimaryAction` (the dashboard's self-view
                    // membership list, `DASHBOARD-12`) still wins on its own; anonymity adds a second
                    // reason for the same demotion rather than a second mechanism.
                    demotePrimaryAction={demotePrimaryAction || readIsTheCardsPrimaryAction}
                    // `DASHBOARD-62`. The footer is `flex-wrap` with the open control carrying
                    // `mr-auto`, and `DASHBOARD-12` already made THAT control icon-only below `sm` so a
                    // long locale could not wrap the row. "Add post" kept its label and took the slack
                    // instead: at 412px the Spanish footer clears by about five pixels, and French
                    // ("Ajouter une publication", 25 chars) is four characters longer than Spanish, so
                    // the row wraps there today. One collapse rule now covers both labels, which is
                    // what stops the open affordance being stranded alone on line one — the exact
                    // composition `DASHBOARD-12`'s icon-only decision was taken to prevent.
                    collapsePrimaryLabelOnMobile={true}
                    surface={surface}
                    localizer={localizer}
                />
            </CardFooter>
            )}
        </Card>
    );
}
